/**
 * Step 2 (Match Teams) of the Build Itinerary wizard.
 *
 * After the import is confirmed into a tournament, this is where a coach links
 * the teams the importer picked up to their EXISTING org teams. Assigning a team
 * re-points every game naming it (via the team-associations endpoint), so that
 * team's roster / starred prospects flow into the itinerary's matched players.
 * Confident name matches are already linked at import; this fixes the rest.
 *
 * You can also follow a team here once it's linked, so its games surface in the
 * itinerary; picking individual players happens on the next step (Follow Players).
 * Leads with a "what we parsed" breakdown so the coach can sanity-check the
 * import before matching teams.
 */

import { useEffect, useRef, useState } from "react";
import { useTournamentGames } from "./useTournamentGames";
import { WizardFooter } from "./WizardFooter";
import { ParseSummary, TeamAssociationPanel } from "./TeamsStep";
import { distinctTournamentTeams } from "./itinerary";
import { autoResolveTeams } from "../../../../../services/tournament-games";
import { useTour } from "../../../../tours/useTour";
import { BUILD_ITINERARY_FEATURE, BUILD_ITINERARY_TOURS } from "./buildItineraryTours";

interface MatchTeamsStepProps {
  /** The confirmed tournament. Absent until Step 1 imports + confirms. */
  calendarEventId: string | undefined;
  onNext: () => void;
  onBack?: () => void;
  canGoBack?: boolean;
}

export function MatchTeamsStep({
  calendarEventId,
  onNext,
  onBack,
  canGoBack,
}: MatchTeamsStepProps) {
  // Bumped after an association so the games (and their matched players) refetch.
  const [reloadToken, setReloadToken] = useState(0);
  const [autoResolving, setAutoResolving] = useState(false);
  const gamesState = useTournamentGames(calendarEventId, reloadToken);
  // Auto match-or-create teams runs at most once per tournament.
  const autoResolvedForRef = useRef<string | null>(null);

  // On open, auto-link every unlinked team to an existing org team THAT HAS PLAYERS
  // (server-side, atomic + idempotent) so a distributed copy (team FKs stripped on
  // distribution) links up where it can. Names with no rostered match are left
  // UNMATCHED (raw name kept, no team created); the coach links or creates those by
  // hand below. Runs once per tournament; skips when everything is already linked.
  useEffect(() => {
    if (!calendarEventId || gamesState.status !== "ready") return;
    if (autoResolvedForRef.current === calendarEventId) return;
    autoResolvedForRef.current = calendarEventId;
    const hasUnlinked = distinctTournamentTeams(gamesState.games).some((t) => !t.teamId);
    if (!hasUnlinked) return;
    setAutoResolving(true);
    autoResolveTeams(calendarEventId)
      .then(() => setReloadToken((t) => t + 1))
      .catch(() => {
        /* non-fatal: the coach can still link teams by hand below */
      })
      .finally(() => setAutoResolving(false));
  }, [calendarEventId, gamesState]);

  // Not while the games load or the auto-link is re-pointing teams, since both replace the list.
  useTour(BUILD_ITINERARY_FEATURE, {
    ready: Boolean(calendarEventId) && gamesState.status === "ready" && !autoResolving,
    hasData: false,
    pageTourKey: BUILD_ITINERARY_TOURS.matchTeams,
    dataTourKey: null,
  });

  if (!calendarEventId) {
    return (
      <div className="wiz-error-box" data-testid="match-teams-no-tournament">
        <p>
          The imported schedule has not been confirmed into a tournament yet.
          Finish importing on the Event step first.
        </p>
      </div>
    );
  }

  if (gamesState.status === "loading") {
    return (
      <div className="wiz-players-state" data-testid="match-teams-loading">
        <span className="wiz-spinner" aria-hidden="true" />
        <span>Loading the tournament's teams...</span>
      </div>
    );
  }

  if (gamesState.status === "error") {
    return (
      <div className="wiz-error-box" data-testid="match-teams-error">
        <p role="alert">{gamesState.message}</p>
      </div>
    );
  }

  const games = gamesState.status === "ready" ? gamesState.games : [];

  return (
    <div data-testid="match-teams-step">
      <h2 className="wiz-step-title">Step 2: Match Teams</h2>
      <p className="wiz-sub">
        Link the teams from this schedule to your existing teams, and follow the ones you want to
        track. A linked team's players and starred prospects show up in your itinerary.
      </p>

      <WizardFooter
        onBack={onBack}
        canGoBack={canGoBack}
        legend="Linking a team pulls its starred players into your itinerary; follow the teams you want to track. You can skip and do this later."
      >
        <button
          type="button"
          className="wiz-btn wiz-btn--primary"
          onClick={onNext}
          data-testid="match-teams-next"
        >
          Next: Follow Players →
        </button>
      </WizardFooter>

      {autoResolving && (
        <div className="wiz-players-state" data-testid="match-teams-auto-resolving">
          <span className="wiz-spinner" aria-hidden="true" />
          <span>Linking teams to your organization...</span>
        </div>
      )}

      <ParseSummary games={games} />
      <TeamAssociationPanel
        calendarEventId={calendarEventId}
        games={games}
        mode="match"
        tourAnchorId="build-itinerary.match.list"
        onAssociated={() => setReloadToken((t) => t + 1)}
      />
    </div>
  );
}
